import '../../../../components/content/ContentTitle.css';
import { invalidateHomePersonalization } from '../../../home/homeRefresh';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWebPreview } from '../../../../features/preview/PreviewControls';
import {
  canPreview,
  previewKey,
  previewTrack,
  webPreviewPlayer,
} from '../../../../features/preview/previewPlayer';
import { useUserStore } from '../../../../stores/userStore';
import { useWebLocale } from '../../../../contexts/WebLocaleContext';
import { saveCurrentPathAndRedirectToLogin } from '../../../../utils/authUtils';
import TrackRatingEditor from './TrackRatingEditor';
import {
  loadMyTrackRatings,
  persistTrackRatings,
  prepareTrackRatings,
  trackSummary,
} from './trackRatingModel';
import './Tracklist.css';

export default function Tracklist(props) {
  const user = useUserStore((s) => s.user);
  const identity = `${props.album?.albumSource}:${props.album?.sourceId || props.album?.id || props.album?.slug}`;
  return <AlbumTracks key={`${identity}:${user?.id ?? 'guest'}`} {...props} user={user} />;
}
function AlbumTracks({ tracks = [], album = {}, showHeading = true, user }) {
  const { locale } = useWebLocale();
  const en = locale === 'en';
  const navigate = useNavigate();
  const client = useQueryClient();
  const location = useLocation();
  const consumedEntry = useRef(null);
  const state = useWebPreview();
  const owner = `album:${album.id || album.slug || album.sourceId || 'detail'}`;
  const mounted = useRef(true);
  const opening = useRef(false);
  const [loading, setLoading] = useState(false);
  const [editor, setEditor] = useState(null);
  const [error, setError] = useState(false);
  const [summaries, setSummaries] = useState({});
  const queue = useMemo(() => tracks.map((t) => previewTrack(t, album)), [tracks, album]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      webPreviewPlayer.dismissOwned(owner);
    };
  }, [owner]);
  const assertCurrent = useCallback(() => {
    if (!mounted.current || !user || String(useUserStore.getState().user?.id) !== String(user.id))
      throw new Error('Account or album changed');
  }, [user]);
  const mine = useQuery({
    queryKey: ['myTrackRatings', user?.id],
    queryFn: () => loadMyTrackRatings(assertCurrent),
    enabled: Boolean(user),
    retry: false,
    staleTime: 30000,
  });
  const open = useCallback(
    async (track) => {
      if (!user) {
        saveCurrentPathAndRedirectToLogin(navigate);
        return;
      }
      if (opening.current) return;
      opening.current = true;
      setLoading(true);
      setError(false);
      try {
        const prepared = await prepareTrackRatings({ ...album, tracks }, track, assertCurrent);
        assertCurrent();
        setSummaries(Object.fromEntries(prepared.album.tracks.map((t) => [t.id, t])));
        setEditor(prepared);
      } catch {
        if (mounted.current) setError(true);
      } finally {
        opening.current = false;
        if (mounted.current) setLoading(false);
      }
    },
    [album, tracks, user, navigate, assertCurrent],
  );
  useEffect(() => {
    const entry = location.state?.recordTrackRating;
    if (!entry || consumedEntry.current === location.key || !tracks.length) return;
    consumedEntry.current = location.key;
    const nextState = { ...location.state };
    delete nextState.recordTrackRating;
    navigate(location.pathname + location.search + location.hash, {
      replace: true,
      state: nextState,
    });
    const target = tracks.find(
      (t) => Number.isSafeInteger(entry.trackId) && t.id === entry.trackId,
    );
    if (user?.id != null && String(entry.viewerId) === String(user.id) && target) void open(target);
  }, [location, navigate, open, tracks, user]);
  const save = async (updates) => {
    const result = await persistTrackRatings(updates, assertCurrent);
    assertCurrent();
    setSummaries((previous) => ({
      ...previous,
      ...Object.fromEntries(
        result.successes.map((s) => [s.trackId, { ...s.summary, userRating: s.rating }]),
      ),
    }));
    if (result.successes.length) void invalidateHomePersonalization(client);
    // Public aggregates and private archives can change even when a batch partially fails.
    for (const key of [
      'albumDetail',
      'me',
      'userContent',
      'search',
      'users',
      'discovery',
      'recommendedUsers',
      'myTrackRatings',
      'myMusicRecords',
      'publicMusicRecords',
    ])
      void client.invalidateQueries({ queryKey: [key] });
    return result;
  };
  return (
    <section className="pp-tracklist" aria-label={en ? 'Tracklist' : '트랙리스트'}>
      {showHeading && <h3>{en ? 'Tracklist' : '트랙리스트'}</h3>}
      <div className="pp-track-rows">
        {queue.map((track, index) => {
          const summary = summaries[track.id] ?? track;
          const active = state.track && previewKey(state.track) === previewKey(track);
          const available = canPreview(track);
          const running = active && state.status === 'playing';
          const artist =
            track.artistName?.trim() ||
            album.artist?.name?.trim() ||
            (en ? 'Unknown artist' : '아티스트 정보 없음');
          const seconds = Math.round((Number(track.durationMs) || 0) / 1000);
          const ratingLabel =
            summary.ratingCount > 0
              ? `${Number(summary.averageRating ?? 0).toFixed(1)} (${summary.ratingCount})`
              : en
                ? 'Rate Track'
                : '평가하기';
          return (
            <div
              className="pp-track-row"
              key={`${track.discNumber}:${track.trackNumber}:${track.sourceId ?? index}`}
            >
              <button
                type="button"
                className={`pp-track-main ${active ? 'is-active' : ''}`}
                disabled={!available}
                aria-label={`${track.title} · ${artist}${available ? (en ? ' · Play preview' : ' · 미리듣기') : ''}`}
                onClick={() => {
                  if (active) webPreviewPlayer.toggle();
                  else void webPreviewPlayer.play(track, queue, owner);
                }}
              >
                <span className="pp-track-number">
                  {active ? (
                    <span
                      aria-hidden="true"
                      className={`pp-track-equalizer ${running ? 'is-running' : ''}`}
                    >
                      <i />
                      <i />
                      <i />
                    </span>
                  ) : (
                    String(track.trackNumber).padStart(2, '0')
                  )}
                </span>
                <span className="pp-track-copy">
                  <span className="pp-track-title-row">
                    <span className="pp-track-title">
                      {track.title}
                      {track.contentRating === 'EXPLICIT' && (
                        <span
                          className="pp-track-explicit pp-inline-explicit"
                          aria-label="Explicit"
                        >
                          E
                        </span>
                      )}
                    </span>
                    <span className="pp-track-duration">
                      {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}
                    </span>
                  </span>
                  <span className="pp-track-artist">{artist}</span>
                </span>
              </button>
              <button
                type="button"
                className="pp-track-rate"
                disabled={loading}
                aria-label={`${track.title} · ${ratingLabel} · ${en ? 'Open track rating' : '곡 평가 열기'}`}
                aria-pressed={Boolean(
                  user &&
                    (summaries[track.id]
                      ? summaries[track.id].userRating != null
                      : mine.data?.some((r) => r.trackId === track.id)),
                )}
                onClick={() => open(track)}
              >
                {summary.ratingCount > 0 && (
                  <span className="pp-track-average-star" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path
                        transform="translate(0 .955)"
                        fill="currentColor"
                        d="M12 2L14.47 8.6L21.51 8.91L15.99 13.3L17.88 20.09L12 16.2L6.12 20.09L8.01 13.3L2.49 8.91L9.53 8.6L12 2Z"
                      />
                    </svg>
                  </span>
                )}
                <span className={summary.ratingCount > 0 ? 'pp-track-score' : ''}>
                  {ratingLabel}
                </span>
              </button>
            </div>
          );
        })}
      </div>
      <p className="pp-track-summary">{trackSummary(tracks, album.totalTracks, en)}</p>
      {loading && <p role="status">{en ? 'Loading ratings…' : '곡 평점을 불러오는 중…'}</p>}
      {error && (
        <p role="alert" className="pp-track-error">
          {en
            ? 'Could not load track ratings. Please try again.'
            : '곡 평점을 불러오지 못했어요. 다시 시도해 주세요.'}
        </p>
      )}
      {editor && (
        <TrackRatingEditor {...editor} en={en} onClose={() => setEditor(null)} onSave={save} />
      )}
    </section>
  );
}
